"use client";

import { useResponsive } from "@/hooks/useResponsive";
import {
  animate,
  motion,
  useMotionValue,
  PanInfo,
  useMotionValueEvent,
} from "framer-motion";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import { useEffect, useRef, useState } from "react";
import SectionTitle from "@/components/SectionTitle";
import clsx from "clsx";
import GalleryModal, { MediaItem } from "../Layouts/components/GalleryModal";
import { parseGallery } from "@/utils/parseProcessData";
import sanitizeDOMData from "@/utils/sanitizeDOMData";

export type BusinessField = {
  id: number | string;
  title: string;
  desc: string;
  image: string;
  _raw: any;
};

type ItemProps = {
  item: BusinessField;
  isDesktop: boolean;
  isDragging: boolean;
  itemWidth?: number;
};

function BusinessSliderItem({ item, isDesktop, isDragging, itemWidth }: ItemProps) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [gallery, setGallery] = useState<MediaItem[]>([]);
  const isActive = isDesktop ? hover : true;

  const handleOpen = (e?: React.MouseEvent) => {
    if (isDragging) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    if (!gallery.length) {
      const parsed = parseGallery(item._raw);
      setGallery(parsed);
    }

    setOpen(true);
  };

  return (
    <>
      <div
        style={itemWidth ? { width: `${itemWidth}px` } : undefined}
        className={clsx(
          "w-[300px] h-[380px] shrink-0 transition-all duration-300 rounded-xl overflow-hidden shadow-md border border-black/5 bg-white",
          isDesktop && "lg:hover:-translate-y-1",
        )}
      >
        <div
          onMouseEnter={() => isDesktop && setHover(true)}
          onMouseLeave={() => isDesktop && setHover(false)}
          className="relative h-full flex flex-col p-5 bg-white border-t-4 border-primary overflow-hidden cursor-pointer group"
        >
          {isDesktop && (
            <div className="absolute inset-0 bg-primary origin-top scale-y-0 transition-transform duration-500 group-hover:scale-y-100 z-[1]" />
          )}

          <div className="relative z-10 flex flex-col h-full">
            <h3
              className={clsx(
                "text-xl font-bold leading-snug mb-3",
                isDesktop
                  ? hover
                    ? "text-white"
                    : "text-black"
                  : "text-black",
              )}
            >
              {item.title}
            </h3>

            <div
              onClick={(e) => handleOpen(e)}
              className="relative flex-1 overflow-hidden flex flex-col justify-end cursor-pointer rounded-lg"
            >
              <SmoothImage
                src={item.image}
                alt={item.title}
                fill
                sizes="300px"
                className={clsx(
                  "object-cover",
                  isDesktop && "transition-opacity duration-300",
                  isDesktop
                    ? hover
                      ? "opacity-0"
                      : "opacity-100"
                    : "opacity-100",
                )}
              />

              {isActive && (
                <div className="absolute inset-0 z-20 flex flex-col justify-end gap-3 p-4 text-white bg-gradient-to-t from-black/85 via-black/35 to-transparent">
                  {isDesktop && (
                    <p className="text-xs leading-relaxed line-clamp-4 font-light text-white/90">
                      {item.desc}
                    </p>
                  )}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(e);
                      setHover(false);
                    }}
                    className={clsx(
                      "font-bold text-xs uppercase tracking-wider flex items-center gap-1 text-third hover:text-white transition duration-200",
                    )}
                  >
                    Xem chi tiết →
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GalleryModal
        open={open}
        onClose={() => setOpen(false)}
        media={gallery}
      />
    </>
  );
}

interface BusinessSliderProps {
  businessFields: BusinessField[];
  title: string;
  desc: string;
  faqList?: { question: string; answer: string }[];
  faqTitle?: string;
  faqSubTitle?: string;
}

export default function BusinessSlider({
  businessFields,
  title,
  desc,
  faqList = [],
  faqTitle,
  faqSubTitle,
}: BusinessSliderProps) {
  const { isDesktop } = useResponsive();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);

  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const REAL_COUNT = businessFields.length;
  const sliderItems = businessFields;
  const itemsPerPage =
    containerWidth < 640 ? 1 : containerWidth < 1024 ? 2 : 3;
  const GAP = 24;
  const itemWidth =
    containerWidth > 0
      ? (containerWidth - (itemsPerPage - 1) * GAP) / itemsPerPage
      : 280;
  const COL_WIDTH = itemWidth + GAP;

  const maxCol = Math.max(0, REAL_COUNT - itemsPerPage);
  const maxScrollX = Math.max(0, maxCol * COL_WIDTH);
  const totalDots = REAL_COUNT > itemsPerPage ? maxCol + 1 : 0;

  useMotionValueEvent(x, "change", (latest) => {
    let col = Math.round(Math.abs(latest) / COL_WIDTH);
    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    setTimeout(() => {
      setIsDragging(false);
    }, 50);

    const rawX = x.get();
    const clamped = Math.max(-maxScrollX, Math.min(0, rawX));
    let col = Math.round(Math.abs(clamped) / COL_WIDTH);

    if (
      Math.abs(info.offset.x) > COL_WIDTH * 0.25 ||
      Math.abs(info.velocity.x) > 600
    ) {
      col += info.offset.x < 0 ? 1 : -1;
    }

    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
    const target = Math.max(-maxScrollX, Math.min(0, -col * COL_WIDTH));

    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const handleGoTo = (idx: number) => {
    if (isDragging) return;
    const clampedIdx = Math.max(0, Math.min(idx, maxCol));
    setActiveIndex(clampedIdx);
    const target = Math.max(-maxScrollX, Math.min(0, -clampedIdx * COL_WIDTH));
    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const toggleFaq = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const showFaq = faqList && faqList.length > 0;
  const showSlider = businessFields && businessFields.length > 0;

  if (!showFaq && !showSlider) return null;

  return (
    <section className="py-12 md:py-16 bg-transparent border-t border-black/5">
      <div className="space-y-8 w-full px-6 md:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* LEFT COLUMN: Business Slides Slider Area (Transparent) */}
          {showSlider && (
            <div
              className={clsx(
                "flex flex-col justify-start relative overflow-hidden bg-transparent",
                showFaq
                  ? "lg:col-span-8 lg:border-r lg:border-black/10 lg:pr-10 border-b border-black/10 pb-10 lg:border-b-0 lg:pb-0"
                  : "lg:col-span-12",
              )}
            >
              <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <SectionTitle
                    title={title || "TIẾN ĐỘ DỰ ÁN"}
                    subtitle="Lướt để khám phá"
                    align="left"
                    theme="dark"
                    subtitleColor="green"
                    underlineColor="green"
                  />
                </div>
                <p className="text-xs text-black/55 max-w-md font-medium leading-relaxed">
                  {desc}
                </p>
              </div>

              <div className="relative w-full flex-grow overflow-hidden -mx-2 px-2 -my-3 py-3">
                <div
                  ref={containerRef}
                  className="w-full overflow-x-hidden overflow-y-visible py-2"
                >
                  <motion.div
                    className="slider flex gap-6"
                    drag="x"
                    dragDirectionLock
                    dragMomentum
                    dragElastic={0.08}
                    dragTransition={{ power: 0.2, timeConstant: 200 }}
                    dragConstraints={{ left: -maxScrollX, right: 0 }}
                    style={{ x }}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={handleDragEnd}
                  >
                    {sliderItems.map((item) => (
                      <BusinessSliderItem
                        key={item.id}
                        item={item}
                        isDesktop={isDesktop}
                        isDragging={isDragging}
                        itemWidth={itemWidth}
                      />
                    ))}
                  </motion.div>
                </div>

                {/* Pagination Dots */}
                {totalDots > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-2">
                    {Array.from({ length: totalDots }).map((_, idx) => {
                      const isActive = activeIndex === idx;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleGoTo(idx)}
                          className={clsx(
                            "h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none",
                            isActive
                              ? "w-7 bg-primary"
                              : "w-2 bg-black/20 hover:bg-black/40",
                          )}
                          aria-label={`Go to slide ${idx + 1}`}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RIGHT COLUMN: FAQ Area (Card Frame) */}
          {showFaq && (
            <div
              className={clsx(
                "flex flex-col justify-start bg-white p-6 md:p-8 rounded-xl shadow-md border-t-4 border-primary",
                showSlider ? "lg:col-span-4" : "lg:col-span-12",
              )}
            >
              <SectionTitle
                title={faqTitle || "Câu Hỏi Thường Gặp"}
                subtitle={faqSubTitle || "Giải đáp thắc mắc"}
                align="left"
                theme="dark"
                subtitleColor="primaryHover"
                underlineColor="primaryHover"
                className="mb-6"
              />

              <div className="space-y-2 flex-grow overflow-y-auto">
                {faqList.map((item, idx) => {
                  const isOpen = openIndex === idx;
                  return (
                    <div key={idx} className="border-b border-gray-100 pb-1">
                      <button
                        onClick={() => toggleFaq(idx)}
                        className="w-full text-left py-3 flex justify-between items-center focus:outline-none group"
                      >
                        <span
                          className={`font-semibold text-xs md:text-sm transition-colors duration-300 ${
                            isOpen
                              ? "text-primary"
                              : "text-gray-800 group-hover:text-primary"
                          }`}
                        >
                          {item.question}
                        </span>
                        <span
                          className={`text-base text-gray-400 font-light transition-transform duration-300 ease-in-out ml-4 ${
                            isOpen ? "rotate-45 !text-primary" : ""
                          }`}
                        >
                          +
                        </span>
                      </button>
                      <div
                        className="grid transition-all duration-300 ease-in-out overflow-hidden"
                        style={{
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                        }}
                      >
                        <div className="overflow-hidden">
                          <p className="pb-3 text-gray-500 font-light leading-relaxed text-[11px] md:text-xs text-justify">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
